// The organization fields needed to pick a tenant. Kept structural so the
// API's full organization type satisfies it without importing React code.
// `/me/organizations` sends `id` as a number even where it is typed as a
// string, so ids are compared and returned through `String()`.
export type TenantOrganization = {
  id: string | number;
  slug: string;
  uuid?: string;
};

export type TenantSelection = {
  tenantId: string;
  // Unknown when the token's org is not among the orgs we were able to list.
  slug: string | undefined;
};

// Reads the organization an access token is bound to (its `tid` claim) from
// the JWT payload. The signature is not verified here: the server already did,
// and this only decides which Tenant-Id header to send. Returns undefined for
// anything that is not a JWT carrying a usable `tid`, and never throws.
// Kept in step with `getTokenTenantId` in `bot/services/tokenTenant.ts`: the two
// packages share no code, so a change to how `tid` is read belongs in both.
export function getTokenTenantId(
  token: string | undefined
): string | undefined {
  const payloadSegment = token?.split('.')[1];
  if (!payloadSegment) {
    return undefined;
  }

  try {
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload: unknown = JSON.parse(atob(padded));

    if (typeof payload !== 'object' || payload === null) {
      return undefined;
    }

    const tid = (payload as { tid?: unknown }).tid;
    if (typeof tid === 'string' && tid !== '') {
      return tid;
    }
    if (typeof tid === 'number' && Number.isFinite(tid)) {
      return String(tid);
    }
    return undefined;
  } catch {
    // A malformed token is an expected input here, not a failure: the caller
    // falls back to choosing a tenant from the user's organizations.
    return undefined;
  }
}

// After the token is re-fetched, decides whether the tab's tenant must follow
// it: the token's org, when it differs from the tenant the tab is sending (the
// user switched organizations from another window or device), else undefined.
// The API rejects a Tenant-Id that differs from the token's org.
export function getTenantToAdopt(
  token: string | undefined,
  currentTenantId: string | undefined
): string | undefined {
  const tokenTenantId = getTokenTenantId(token);
  return tokenTenantId !== undefined && tokenTenantId !== currentTenantId
    ? tokenTenantId
    : undefined;
}

// Chooses the tenant to send as the Tenant-Id header. The API rejects a header
// that differs from the org the token is bound to, so the token's org wins over
// the persisted tenant and the org list order. Without a token org, keep the
// persisted tenant if the user still belongs to it, else the first org.
export function resolveTenant({
  tokenTenantId,
  persistedTenantId,
  orgs,
}: {
  tokenTenantId: string | undefined;
  persistedTenantId: string | undefined;
  orgs: TenantOrganization[];
}): TenantSelection {
  if (tokenTenantId !== undefined) {
    const tokenOrg = orgs.find(
      (org) => String(org.id) === tokenTenantId || org.uuid === tokenTenantId
    );
    // The org list is capped, so the token's org may be missing from it; the
    // header must still match the token.
    return tokenOrg
      ? { tenantId: String(tokenOrg.id), slug: tokenOrg.slug }
      : { tenantId: tokenTenantId, slug: undefined };
  }

  const effectiveOrg =
    orgs.find((org) => String(org.id) === persistedTenantId) ?? orgs[0];
  if (!effectiveOrg) {
    throw new Error('User belongs to no organizations');
  }
  return { tenantId: String(effectiveOrg.id), slug: effectiveOrg.slug };
}
